import React, { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchMyActiveJobs } from "../../services/firestore";
import { enableNetwork, disableNetwork } from "firebase/firestore";
import { db } from "../../firebase";
import "./GuardLayout.css";

const TABS = [
  { to: "/guard/pool",     icon: "🔍", label: "Pool" },
  { to: "/guard/jobs",     icon: "📋", label: "My Jobs",  badgeKey: "myJobs" },
  { to: "/guard/earnings", icon: "💰", label: "Earnings" },
  { to: "/guard/profile",  icon: "👤", label: "Profile" },
];

// True iff the YYYY-MM-DD string is tomorrow in the local timezone.
function isTomorrow(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return false;
  const target = new Date(y, m - 1, d);
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return target.getFullYear() === tomorrow.getFullYear()
      && target.getMonth() === tomorrow.getMonth()
      && target.getDate() === tomorrow.getDate();
}

// True iff the YYYY-MM-DD string is today in the local timezone.
function isToday(dateStr) {
  if (!dateStr) return false;
  const [y, m, d] = String(dateStr).split("-").map(Number);
  if (!y || !m || !d) return false;
  const t = new Date();
  return y === t.getFullYear() && m - 1 === t.getMonth() && d === t.getDate();
}

export default function GuardLayout() {
  const { userProfile, isOwner } = useAuth();
  const navigate = useNavigate();
  const [online, setOnline] = useState(navigator.onLine);
  // Tab-bar badge counters. Drive an unread-style red dot on My Jobs
  // so a guard who lives on the Pool tab still sees pending rep-confirms
  // and ready-to-start jobs without us relying on push notifications
  // (which can fail silently — bad token, browser permission revoked,
  // OS quiet hours, etc.). Belt-and-suspenders for the day-before flow.
  const [tabCounts, setTabCounts] = useState({ myJobs: 0 });

  // ── Text size ────────────────────────────────────────────────────
  // The app uses fixed px fonts, so a phone's system "larger text" setting
  // doesn't scale it (iOS ignores it for web entirely). Give guards their own
  // control that zooms the whole guard UI. Per-device (localStorage) — it's
  // about this phone / these eyes, not the account.
  const TEXT_SIZES = [
    { v: 1,    label: "A",  name: "Normal" },
    { v: 1.15, label: "A",  name: "Large" },
    { v: 1.3,  label: "A",  name: "Larger" },
    { v: 1.5,  label: "A",  name: "Largest" },
  ];
  const [textScale, setTextScale] = useState(() => {
    const v = Number(localStorage.getItem("guardTextScale"));
    return TEXT_SIZES.some((s) => s.v === v) ? v : 1;
  });
  const [sizeOpen, setSizeOpen] = useState(false);
  function changeScale(v) {
    setTextScale(v);
    try { localStorage.setItem("guardTextScale", String(v)); } catch { /* private mode */ }
  }

  // ── Pull-to-refresh ──────────────────────────────────────────────
  // Installed standalone PWAs (added to the home screen) lose the
  // browser's native pull-to-refresh, so guards had no way to force a
  // re-sync when a dropped live connection left stale data on screen.
  // We implement the gesture ourselves on the scroll container: pull
  // down past a threshold while scrolled to the top, release, and the
  // app reloads (the most reliable way to re-establish Firestore's
  // listeners). The experimentalAutoDetectLongPolling setting should
  // keep this from being needed often — this is the manual fallback.
  const contentRef = useRef(null);
  const [pull, setPull] = useState(0);          // current pull distance (px)
  const [refreshing, setRefreshing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const pullRef = useRef(0);                     // latest distance for touchend
  const startYRef = useRef(null);
  const pullingRef = useRef(false);
  const THRESHOLD = 70;                          // px to trigger a refresh

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const MAX = 110;
    const RESIST = 0.5;

    const onStart = (e) => {
      if (refreshing) return;
      if (el.scrollTop <= 0) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = true;
        setDragging(true);
      } else {
        pullingRef.current = false;
      }
    };
    const onMove = (e) => {
      if (!pullingRef.current || refreshing) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy > 0 && el.scrollTop <= 0) {
        const dist = Math.min(dy * RESIST, MAX);
        pullRef.current = dist;
        setPull(dist);
        if (e.cancelable) e.preventDefault(); // suppress iOS rubber-band
      } else if (dy <= 0) {
        pullingRef.current = false;
        pullRef.current = 0;
        setPull(0);
      }
    };
    const onEnd = () => {
      if (!pullingRef.current) return;
      pullingRef.current = false;
      setDragging(false);
      if (pullRef.current >= THRESHOLD) {
        setRefreshing(true);
        setPull(THRESHOLD);
        // Brief pause so the spinner is visible, then reload to rebuild
        // the Firestore connection and pull fresh data.
        setTimeout(() => window.location.reload(), 500);
      } else {
        pullRef.current = 0;
        setPull(0);
      }
    };

    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchmove", onMove, { passive: false });
    el.addEventListener("touchend", onEnd);
    el.addEventListener("touchcancel", onEnd);
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchmove", onMove);
      el.removeEventListener("touchend", onEnd);
      el.removeEventListener("touchcancel", onEnd);
    };
  }, [refreshing]);

  useEffect(() => {
    const handleOnline  = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener("online",  handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online",  handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // ── Re-sync on app focus ─────────────────────────────────────────
  // iOS suspends a backgrounded PWA's network connection, which freezes
  // Firestore's live listeners — so a change made while the phone was set
  // down (e.g. the owner confirming a grab on another device) never
  // reaches the guard until a manual refresh. When the app becomes
  // visible/focused again, toggle the Firestore network off→on to force
  // a reconnect; all active onSnapshot listeners then re-deliver fresh
  // server data with no page reload. Guarded so overlapping focus +
  // visibilitychange events don't run it twice at once.
  useEffect(() => {
    let resyncing = false;
    const resync = async () => {
      if (document.visibilityState !== "visible") return;
      if (resyncing) return;
      resyncing = true;
      try {
        await disableNetwork(db);
        await enableNetwork(db);
      } catch (err) {
        console.warn("Focus re-sync failed", err);
      } finally {
        resyncing = false;
      }
    };
    document.addEventListener("visibilitychange", resync);
    window.addEventListener("focus", resync);
    return () => {
      document.removeEventListener("visibilitychange", resync);
      window.removeEventListener("focus", resync);
    };
  }, []);

  // Watch the guard's active jobs and surface a badge count of "things
  // that need your attention." Two buckets count toward the badge:
  //   1. Tomorrow's jobs needing rep-confirm (haven't been texted yet).
  //   2. Today's jobs that aren't already in progress (waiting to start).
  // Both are situations where the push notification is the primary cue
  // and the badge is the visual fallback.
  useEffect(() => {
    if (!userProfile?.id) return;
    const unsub = watchMyActiveJobs(userProfile.id, (jobs) => {
      const needsConfirm = jobs.filter((j) =>
        isTomorrow(j.scheduledDate)
        && j.salesRepPhone
        && !j.repConfirmedAt
        && j.requiresRepConfirm !== false
      ).length;
      const todayPending = jobs.filter((j) =>
        isToday(j.scheduledDate)
        && (j.status === "confirmed" || j.status === "grabbed")
      ).length;
      setTabCounts({ myJobs: needsConfirm + todayPending });
    });
    return unsub;
  }, [userProfile?.id]);

  return (
    <div className="guard-shell">
      {isOwner && (
        <div style={{
          background: "#0B1C2E", color: "#8ab4d4",
          padding: "8px 16px", fontSize: 12,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <span>👁 Owner preview — guard view</span>
          <button
            onClick={() => navigate("/owner/inbox")}
            style={{
              background: "rgba(255,255,255,0.12)", border: "none",
              color: "#fff", borderRadius: 6, padding: "4px 12px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
            }}
          >
            ← Back to owner view
          </button>
        </div>
      )}
      <div className="guard-topbar">
        <span className="guard-topbar-logo">VeloceFlow</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!online && <span className="guard-topbar-offline">● Syncing...</span>}
          {/* Text size — zooms the whole guard UI for readability. */}
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setSizeOpen((o) => !o)}
              title="Text size"
              aria-label="Text size"
              style={{
                background: "rgba(255,255,255,0.14)", border: "none", color: "#fff",
                borderRadius: 6, height: 28, padding: "0 9px", cursor: "pointer",
                fontWeight: 700, lineHeight: 1, display: "flex", alignItems: "center", gap: 2,
              }}
            >
              <span style={{ fontSize: 11 }}>A</span><span style={{ fontSize: 15 }}>A</span>
            </button>
            {sizeOpen && (
              <>
                <div onClick={() => setSizeOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
                <div style={{
                  position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 41,
                  background: "#fff", borderRadius: 10, boxShadow: "0 6px 20px rgba(0,0,0,0.22)",
                  border: "1px solid #e0e0e0", padding: 6, minWidth: 150,
                }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#888", padding: "4px 8px" }}>TEXT SIZE</div>
                  {TEXT_SIZES.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => { changeScale(s.v); setSizeOpen(false); }}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                        width: "100%", textAlign: "left", background: textScale === s.v ? "#eef3fb" : "transparent",
                        border: "none", borderRadius: 6, padding: "8px 10px", cursor: "pointer",
                        color: "#111", fontWeight: textScale === s.v ? 700 : 500,
                      }}
                    >
                      <span style={{ fontSize: Math.round(13 * s.v) }}>{s.name}</span>
                      {textScale === s.v && <span style={{ color: "#185FA5" }}>✓</span>}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          <span className="guard-topbar-name">{userProfile?.name?.split(" ")[0] || "Guard"}</span>
        </div>
      </div>

      <div className="guard-content" ref={contentRef}>
        {/* Pull-to-refresh indicator. Its height tracks the pull distance
            so content slides down under the finger; snaps back with a
            transition when released. */}
        <div
          style={{
            height: refreshing ? THRESHOLD : pull,
            transition: dragging ? "none" : "height 0.25s ease",
            overflow: "hidden",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#185FA5", fontSize: 12, fontWeight: 600,
          }}
        >
          {pull > 0 || refreshing ? (
            <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {refreshing ? (
                <>
                  <span className="ptr-spinner" /> Refreshing…
                </>
              ) : pull >= THRESHOLD ? (
                "↑ Release to refresh"
              ) : (
                "↓ Pull to refresh"
              )}
            </span>
          ) : null}
        </div>
        {/* zoom scales the whole content (fixed px fonts included) to the
            guard's chosen text size. Applied here, not on the scroll
            container, so pull-to-refresh math stays in real pixels. */}
        <div style={textScale !== 1 ? { zoom: textScale } : undefined}>
          <Outlet />
        </div>
      </div>

      <nav className="guard-tabs">
        {TABS.map((tab) => {
          const count = tab.badgeKey ? tabCounts[tab.badgeKey] : 0;
          return (
            <NavLink
              key={tab.to}
              to={tab.to}
              className={({ isActive }) => `guard-tab${isActive ? " active" : ""}`}
            >
              <span className="guard-tab-icon" style={{ position: "relative" }}>
                {tab.icon}
                {/* Unread-style badge — only renders when count > 0. Kept
                    on the icon (not the label) so it sits in roughly the
                    same place across all tabs and reads at a glance. */}
                {count > 0 && (
                  <span style={{
                    position: "absolute", top: -4, right: -10,
                    minWidth: 16, height: 16, padding: "0 4px",
                    background: "#d93025", color: "#fff",
                    borderRadius: 8, fontSize: 10, fontWeight: 700,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    border: "2px solid #fff", lineHeight: 1,
                  }}>
                    {count > 9 ? "9+" : count}
                  </span>
                )}
              </span>
              <span className="guard-tab-label">{tab.label}</span>
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}
